import { forwardRef } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsExporting from 'highcharts/modules/exporting';
import HighchartsExportData from 'highcharts/modules/export-data';
import { colors, fontSizes, fontWeights } from 'ui/theme';
import { NUMBER_SUFFIXES } from '../constants';
import css from './EngagementConversionColumnChart.css';
import { formatPercentage, getMultiConversionSymbol } from '../utils';
import { CONVERSION_SHAPE_SIZES } from '../types';

if (typeof Highcharts === 'object') {
  HighchartsExporting(Highcharts);
  HighchartsExportData(Highcharts);
}

const computeSeriesData = (engagementGraphData) =>
  engagementGraphData.map(({ key, value }, index) => {
    if (Array.isArray(value)) {
      return {
        name: key,
        type: 'column',
        data: value.map(({ eventName, eventValue, color, shape }) => {
          return {
            name: eventName,
            x: index,
            y: eventValue,
            color,
            shape,
          };
        }),
      };
    }
    return {
      name: key,
      color: colors.cyan80,
      type: 'column',
      data: [
        {
          x: index,
          y: value,
        },
      ],
    };
  });

const getHighchartOptions = (engagementGraphData) => {
  return {
    chart: {
      type: 'column',
    },
    title: {
      text: undefined,
    },
    exporting: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    legend: {
      enabled: false,
    },

    xAxis: {
      lineColor: colors.grey15,
      categories: engagementGraphData.map(({ label }) => label),
    },
    yAxis: {
      lineWidth: 1,
      lineColor: colors.grey15,
      gridLineColor: colors.grey15,
      gridLineDashStyle: 'ShortDash',
      endOnTick: false,
      title: {
        text: null,
      },
      stackLabels: {
        enabled: true,
        formatter: function () {
          return this.total.toLocaleString();
        },
        style: {
          color: colors.textPrimary,
          fontSize: fontSizes.s12,
          fontWeight: fontWeights.regular,
        },
      },
      labels: {
        formatter: function () {
          let val = this.value;
          let suffixIndex = 0;

          while (val >= 1000) {
            val /= 1000;
            suffixIndex += 1;
          }

          val += NUMBER_SUFFIXES[suffixIndex];
          return val;
        },
      },
    },
    tooltip: {
      headerFormat: undefined,
      outside: true,
      shared: false,
      useHTML: true,
      padding: 0,
      borderRadius: 10,
      borderColor: colors.grey80,
      backgroundColor: colors.grey80,
      style: {
        color: colors.white,
        fontSize: fontSizes.s12,
        fontWeight: fontWeights.regular,
      },

      pointFormatter: function () {
        const {
          series: {
            name: seriesKey,
            data: stackData,
            yAxis: { series: graphData = [] } = {},
          } = {},
          category: seriesName,
          stackTotal: seriesValue,
          x: currentIndex,
        } = this;
        let tooltipMetricsContent;
        if (seriesKey === 'Conversions') {
          tooltipMetricsContent = [...stackData]
            .map(({ name: conversionName, y: conversionValue, color, shape }) => {
              return `<div class="${css.flex}">
                    <div style="color: ${color}; font-size: ${CONVERSION_SHAPE_SIZES[shape]};">
                      ${getMultiConversionSymbol(shape)}
                    </div>
                    <div class="${css.spacing}">
                      <span>${conversionName}:</span>
                      <b>&nbsp${conversionValue.toLocaleString()}</b>
                    </div>
                  </div>`;
            })
            .reverse()
            .join('');
        } else {
          tooltipMetricsContent = `<div class="${css.flex}">
            <span>${seriesName}:</span>
            <b>&nbsp${seriesValue.toLocaleString()}</b>
          </div>`;
        }

        tooltipMetricsContent = `
        <div class="${css.tooltipContainer}">
          ${tooltipMetricsContent}
        </div>
      `;

        if (currentIndex !== 0) {
          const previousPointData = graphData[currentIndex - 1]?.yData?.[0];
          const dropOffRate = ((previousPointData - seriesValue) / previousPointData) * 100;

          if (dropOffRate > 0) {
            tooltipMetricsContent = `
            ${tooltipMetricsContent}
            <hr class="${css.lineBreak}" />
            <div class="${css.tooltipContainer}">
              <div class="${css.flex}">
                <span>Drop-off rate:</span>
                <b>&nbsp${formatPercentage(dropOffRate)}</b>
              </div>
            </div>
            `;
          }
        }

        return tooltipMetricsContent;
      },
    },
    plotOptions: {
      column: {
        stacking: 'normal',
      },
      series: {
        states: {
          inactive: {
            enabled: false,
          },
        },
      },
    },
    series: computeSeriesData(engagementGraphData),
  };
};

export const EngagementConversionColumnChart = forwardRef(({ data }, ref) => {
  return (
    <HighchartsReact
      options={getHighchartOptions(data)}
      ref={ref}
      containerProps={{ style: { width: '100%', height: '100%' } }}
    />
  );
});
